import React, {useEffect, useState, useRef} from "react";
import setTitle from "../helpers/setTitle";

import {Popover, message, Popconfirm, Skeleton, Space, Table, Tag, Tooltip, Select, DatePicker, Card, Row, Col, Input} from "antd";
import TimeAgo from "../helpers/TimeAgo";
import {Link} from "react-router-dom";
import {EyeOutlined, ClockCircleOutlined, DeleteOutlined, AlertOutlined, DownloadOutlined, FilterOutlined} from "@ant-design/icons";
import Button from "antd/lib/button";
import api from "../api";
import HelpPopover from "../HelpPopover";
import CheckCircleOutlined from "@ant-design/icons/lib/icons/CheckCircleOutlined";
import {LightboxIcon, StorageFolderIcon, UserGroupIcon} from "../helpers/icons";
import {OrgLink} from "../helpers/OrgNavLink";
import {useTranslation} from "react-i18next";
import TableDataDownloadButton from "../widgets/TableDataDownloadButton";
import PaginationTotal from "../widgets/PaginationTotal";
import UploadReportDownloadButton from "./UploadReportDownloadButton";
import moment from "moment";

const { RangePicker } = DatePicker;

export default ()=> {
    const {t} = useTranslation();

    setTitle(t('uploads','Uploads'));

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize: 25});

    const apiPath = '/api/uploads';
    const params = useRef({});
    const [settings, setSettings] = useState({});

    // Filter state
    const [searchFilter, setSearchFilter] = useState('');
    const [dateRangeFilter, setDateRangeFilter] = useState(null);

    const destroy = id => {
        api({
            url: `/api/uploads/${id}`,
            method: 'delete'
        }).then(res => {
            message.success(t('message-upload-deleted','Upload Deleted.'));
            handleTableChange();
        });
    };

    const handleTableChange = (pagination = settings.pagination, filters = settings.filters, sorter = settings.sorter) => {
        setSettings({pagination, filters, sorter});
        params.current = {
            sortField: sorter?.field,
            sortOrder: sorter?.order,
            pagination,
            ...filters,
            contribution_search: searchFilter || null,
            updated_at: dateRangeFilter ? [dateRangeFilter[0].format('YYYY-MM-DD'), dateRangeFilter[1].format('YYYY-MM-DD')] : null,
        };
        loadTableData();
    };

    const loadTableData = () => {
        setLoading(true);
        api.get(apiPath, { params: params.current }).then(res => {
            setData(res.data);
            setPagination({
                ...params.current.pagination,
                total: res.headers['total-entries']
            });
            setLoading(false);
        });
    };

    useEffect(() => {
        handleTableChange(pagination);
    }, []);

    // Debounced search filter
    useEffect(() => {
        if (!settings.pagination) return; // Only trigger after initial load

        const timeoutId = setTimeout(() => {
            // Reset to page 1 when filters change
            handleTableChange({...settings.pagination, current: 1}, settings.filters, settings.sorter);
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [searchFilter]);

    // Date range filter (no debounce)
    useEffect(() => {
        if (settings.pagination) { // Only trigger after initial load
            // Reset to page 1 when filters change
            handleTableChange({...settings.pagination, current: 1}, settings.filters, settings.sorter);
        }
    }, [dateRangeFilter]);

    const columns = [
        {
            title: t('last-updated','Last Updated').toProperCase(),
            dataIndex: 'updated_at',
            sorter: true,
            width: 150,
            render: (updated_at) => <TimeAgo date={updated_at}/>,
        },
        {
            title: t('name','Name'),
            dataIndex: 'name',
            sorter: true,
            render: (name, upload) => (
                <Tooltip title={`ID: ${upload.id}`}>{name}</Tooltip>
            )
        },
        {
            title: t('contribution','Contribution'),
            render: upload => {
                if(!upload.contribution) return (<em>{t('removed','Removed')}</em>)

                const name = upload.contribution.name
                return (
                    <>
                        {upload.contribution.user_group && <Tooltip title={t('user-group','User Group')}><UserGroupIcon/> {name}</Tooltip>}
                        {upload.contribution.asset_group?.type == 'Lightbox' && <Tooltip title={t('lightbox','Lightbox')}><LightboxIcon/> {name}</Tooltip>}
                        {upload.contribution.asset_group?.type == 'StorageFolder' && <Tooltip title={t('storage-folder','Storage Folder')}><StorageFolderIcon/> {name}</Tooltip>}
                    </>
                )
            },
        },
        {
            title: t('total','Total').toProperCase(),
            dataIndex: 'assets_count',
            sorter: true,
            render: (count) => count || 0,
            width: 80,
        },
        {
            title: t('uploaded','Uploaded').toProperCase(),
            dataIndex: 'processed_count',
            sorter: true,
            render: (count) => count || 0,
            width: 90,
        },
        {
            title: t('processing','Processing').toProperCase(),
            dataIndex: 'processing_count',
            sorter: true,
            render: (count) => count || 0,
            width: 100,
        },
        {
            title: t('pending','Pending').toProperCase(),
            dataIndex: 'pending_count',
            sorter: true,
            render: (count) => count || 0,
            width: 90,
        },
        {
            title: t('stalled','Stalled').toProperCase(),
            dataIndex: 'stalled_count',
            sorter: true,
            render: (count) => {
                const val = count || 0;
                return <span style={{color: val > 0 ? '#ff4d4f' : undefined}}>{val}</span>;
            },
            width: 80,
        },
        {
            title: t('errored','Errored').toProperCase(),
            dataIndex: 'errored_count',
            sorter: true,
            render: (count) => {
                const val = count || 0;
                return <span style={{color: val > 0 ? '#ff4d4f' : undefined}}>{val}</span>;
            },
            width: 80,
        },
        {
            title: '',
            width: 150,
            render: upload => (
                <Space>
                    <OrgLink to={`/upload/${upload.contribution?.slug}/${upload.guid}`}>
                        <Button icon={<EyeOutlined/>}>{t('button-view','View')}</Button>
                    </OrgLink>

                    {upload.assets_count > 0 && (
                        <UploadReportDownloadButton uploadId={upload.id} />
                    )}

                    {!upload.assets_count && (
                        <Popconfirm
                            title={t('confirm-delete-empty-upload','Delete Empty Upload?')}
                            onConfirm={() => destroy(upload.id)}
                        >
                            <Button size={'middle'}>
                                <DeleteOutlined/>
                            </Button>
                        </Popconfirm>
                    )}
                </Space>

            )
        }
    ]

    return (
        <div style={{margin:'2em'}}>
            <h1><ClockCircleOutlined/> {t('my-past-uploads','My Past Uploads')} <HelpPopover code={'past-uploads-header'}/></h1>

            <Card
                title={<><FilterOutlined/> {t('filters', 'Filters')}</>}
                size="small"
                style={{marginBottom: 16}}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={12}>
                        <label style={{display: 'block', marginBottom: 8, fontWeight: 500}}>
                            {t('search', 'Search')}
                        </label>
                        <Input.Search
                            placeholder={t('search-uploads', 'Search uploads, contributions, folders...')}
                            style={{ width: '100%' }}
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <label style={{display: 'block', marginBottom: 8, fontWeight: 500}}>
                            {t('date-range', 'Date Range')}
                        </label>
                        <RangePicker
                            style={{ width: '100%' }}
                            value={dateRangeFilter}
                            onChange={setDateRangeFilter}
                            format="YYYY-MM-DD"
                        />
                    </Col>
                </Row>
            </Card>

            <Table
                scroll={{x: true}}
                bordered
                size={'small'}
                rowKey={'id'}
                columns={columns}
                dataSource={data}
                pagination={{...pagination, showSizeChanger: true}}
                loading={loading}
                onChange={handleTableChange}
                footer={() => (
                    <div style={{display:'flex', alignItems:'center'}}>
                        <Space>
                            <TableDataDownloadButton apiPath={apiPath} settings={settings}/>
                        </Space>
                        <PaginationTotal pagination={pagination}/>
                    </div>
                )}
            />
        </div>
    );
}
